import { openDB, type DBSchema } from 'idb';
import type { AppData, SyncMutation } from '../types';

interface TrainingDb extends DBSchema {
  cache: { key: string; value: AppData };
  mutations: { key: string; value: SyncMutation; indexes: { 'by-entity': string; 'by-created': string } };
}

const database = openDB<TrainingDb>('sila-training-v1', 1, {
  upgrade(db) {
    db.createObjectStore('cache');
    const mutations = db.createObjectStore('mutations', { keyPath: 'id' });
    mutations.createIndex('by-entity', 'entityKey');
    mutations.createIndex('by-created', 'createdAt');
  },
});

export async function readCachedData(): Promise<AppData | undefined> {
  return (await database).get('cache', 'bootstrap');
}

export async function cacheData(data: AppData): Promise<void> {
  await (await database).put('cache', data, 'bootstrap');
}

export async function pendingMutations(): Promise<SyncMutation[]> {
  return (await database).getAllFromIndex('mutations', 'by-created');
}

export async function enqueueMutation(mutation: SyncMutation): Promise<void> {
  const db = await database;
  const tx = db.transaction('mutations', 'readwrite');
  const previous = await tx.store.index('by-entity').getAll(mutation.entityKey);
  if (previous.length) {
    mutation.baseVersion = previous[0].baseVersion;
    for (const item of previous) await tx.store.delete(item.id);
  }
  await tx.store.put(mutation);
  await tx.done;
}

export async function removeMutations(ids: string[]): Promise<void> {
  const db = await database;
  const tx = db.transaction('mutations', 'readwrite');
  await Promise.all(ids.map((id) => tx.store.delete(id)));
  await tx.done;
}

export async function rebaseMutation(id: string, serverVersion: number): Promise<void> {
  const db = await database;
  const mutation = await db.get('mutations', id);
  if (!mutation) return;
  await db.delete('mutations', id);
  await db.put('mutations', { ...mutation, id: crypto.randomUUID(), baseVersion: serverVersion, createdAt: new Date().toISOString() });
}

export async function updateEntityBaseVersion(entityKey: string, serverVersion: number): Promise<void> {
  const db = await database;
  const tx = db.transaction('mutations', 'readwrite');
  const mutations = await tx.store.index('by-entity').getAll(entityKey);
  for (const mutation of mutations) await tx.store.put({ ...mutation, baseVersion: serverVersion });
  await tx.done;
}

export async function clearOfflineData(): Promise<void> {
  const db = await database;
  await Promise.all([db.clear('cache'), db.clear('mutations')]);
}
